import { openAccordion, resolveAnchor, submenuAnchor, type AnchorSpec } from '../engine/anchors';
import {
  clickWhenReady,
  fillField,
  findVisible,
  isFilled,
  pressWhenReady,
  waitUntil,
} from './formFill';
import { closeFlowChrome } from './closeChrome';
import { flashAnchor } from './visibleAction';
import type { TaskFlow } from './types';

const FLOWS_LIST_PATHS = ['/home/automation', '/home/automation/flows'];

const SIDEBAR_ROW = submenuAnchor('Automations', 'Automations');

const CREATE_AUTOMATION_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Create automation'] },
};

const NAME_INPUT_SELECTOR = '#new-automation-name';
const CONFIRM_CREATE_BUTTON: AnchorSpec = { selector: '[data-testid="create-automation-confirm"]' };

const WHATSAPP_CHANNEL_ITEM: AnchorSpec = {
  structural: { within: '[role="menu"]', match: '[role="menuitem"]', labels: ['WhatsApp'] },
};

const channelMenuOpen = () => Boolean(resolveAnchor(WHATSAPP_CHANNEL_ITEM));

const PALETTE_SCOPE = '.border-r.bg-card';
const draggableLabelled = (label: string): AnchorSpec => ({
  structural: { within: PALETTE_SCOPE, match: '[draggable="true"]', labels: [label] },
});

const SAVE_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Save'] },
};

const BACK_TO_LIST_BUTTON: AnchorSpec = { selector: 'button:has(svg.lucide-arrow-left)' };

const DISCARD_CHANGES_BUTTON: AnchorSpec = {
  structural: { within: '[role="alertdialog"]', match: 'button', labels: ['Discard changes'] },
};
const KEEP_EDITING_BUTTON: AnchorSpec = {
  structural: { within: '[role="alertdialog"]', match: 'button', labels: ['Keep editing'] },
};

const RUN_TEST_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Run test'], labelWithin: 'span' },
};

const FIRST_ISSUE_FOCUS_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Focus'] },
};

const MESSAGE_FIELD_SELECTOR = '#message';

const DUMMY_MESSAGE = 'Hi! Thanks for reaching out 👋 We got your message and will reply shortly.';

export function isFlowsListPath(pathname: string): boolean {
  return FLOWS_LIST_PATHS.includes(pathname.replace(/\/+$/, '') || '/');
}

const onFlowsListPage = () =>
  isFlowsListPath(window.location.pathname) && Boolean(resolveAnchor(CREATE_AUTOMATION_BUTTON));

const nameDialogOpen = () => Boolean(findVisible(NAME_INPUT_SELECTOR));

const onFlowBuilderCanvas = () =>
  /^\/home\/automation\/flows\/[^/]+$/.test(window.location.pathname) &&
  Boolean(document.querySelector('.react-flow'));

const onUnsavedNewFlow = () => window.location.pathname === '/home/automation/flows/new';

const two = (n: number) => String(n).padStart(2, '0');

function suggestedFlowName(): string {
  const now = new Date();
  return `Welcome flow ${two(now.getHours())}${two(now.getMinutes())}${two(now.getSeconds())}`;
}
const SUGGESTED_NAME = suggestedFlowName();

function hasNodeOfType(nodeType: string): boolean {
  return Boolean(document.querySelector(`.react-flow__node-${nodeType}`));
}

function edgeCount(): number {
  return document.querySelectorAll('.react-flow__edge').length;
}

function sourceHandleOf(nodeType: string): HTMLElement | null {
  return document.querySelector(`.react-flow__node-${nodeType} .react-flow__handle.source`);
}

function targetHandleOf(nodeType: string): HTMLElement | null {
  return document.querySelector(`.react-flow__node-${nodeType} .react-flow__handle.target`);
}

let startToMessageBaseline = 0;
let messageToEndBaseline = 0;

function dropNodeOntoCanvas(nodeType: string, label: string): boolean {
  const canvas = document.querySelector('.react-flow');
  if (!canvas) return false;
  try {
    const dataTransfer = new DataTransfer();
    dataTransfer.setData('application/reactflow', nodeType);
    dataTransfer.setData('application/reactflow-label', label);
    const rect = canvas.getBoundingClientRect();
    const clientX = rect.left + rect.width / 2;
    const clientY = rect.top + rect.height / 2;
    const init: DragEventInit = { bubbles: true, cancelable: true, dataTransfer, clientX, clientY };
    canvas.dispatchEvent(new DragEvent('dragover', init));
    canvas.dispatchEvent(new DragEvent('drop', init));
    return true;
  } catch {
    return false;
  }
}

async function connectHandles(source: HTMLElement, target: HTMLElement): Promise<void> {
  try {
    const srcRect = source.getBoundingClientRect();
    const tgtRect = target.getBoundingClientRect();
    const srcX = srcRect.left + srcRect.width / 2;
    const srcY = srcRect.top + srcRect.height / 2;
    const tgtX = tgtRect.left + tgtRect.width / 2;
    const tgtY = tgtRect.top + tgtRect.height / 2;
    const fire = (el: EventTarget, type: string, x: number, y: number, buttons: number) => {
      el.dispatchEvent(
        new MouseEvent(type, {
          bubbles: true,
          cancelable: true,
          composed: true,
          view: window,
          button: 0,
          buttons,
          clientX: x,
          clientY: y,
        }),
      );
    };

    fire(source, 'mousedown', srcX, srcY, 1);
    const steps = 4;
    for (let i = 1; i <= steps; i += 1) {
      const x = srcX + ((tgtX - srcX) * i) / steps;
      const y = srcY + ((tgtY - srcY) * i) / steps;
      fire(document, 'mousemove', x, y, 1);
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
    }
    fire(document, 'mousemove', tgtX, tgtY, 1);
    fire(document, 'mouseup', tgtX, tgtY, 0);
  } catch {
  }
}

async function openChannelMenuAndPickWhatsApp(): Promise<void> {
  if (nameDialogOpen()) return;

  if (!channelMenuOpen()) {
    if (!(await pressWhenReady(CREATE_AUTOMATION_BUTTON))) {
      console.warn('[tour-guide] "create-automation-flow": could not find/press Create automation.');
      return;
    }
    const appeared = await waitUntil(
      () => nameDialogOpen() || channelMenuOpen(),
      2500,
    );
    if (nameDialogOpen()) return;
    if (!appeared || !channelMenuOpen()) {
      console.warn('[tour-guide] "create-automation-flow": the channel menu never opened.');
      return;
    }
  }

  await flashAnchor(WHATSAPP_CHANNEL_ITEM);
  if (!(await pressWhenReady(WHATSAPP_CHANNEL_ITEM))) {
    console.warn('[tour-guide] "create-automation-flow": could not press the WhatsApp channel item.');
    return;
  }

  if (!(await waitUntil(nameDialogOpen, 2500))) {
    console.warn('[tour-guide] "create-automation-flow": the name dialog never opened.');
  }
}

export const AUTOMATION_FLOW_TASK: TaskFlow = {
  id: 'create-automation-flow',
  stepId: 'automations--automations',
  cta: 'Try it: build an automation flow',
  preparingLabel: 'Opening Automations…',
  title: 'Build an automation flow',
  summary: 'Drag on two nodes, connect them, check for errors, save, and head back to your list.',
  doneTitle: 'Automation flow created',
  doneDescription:
    'Start → Send Message → End, connected, checked and saved. Reopen it any time from your automations list to add more steps, then Publish when you are ready to turn it on.',

  beforeStart: async () => {
    void import('@/pages/automation/Flows').catch(() => {});
    void import('@/pages/automation/FlowBuilder').catch(() => {});

    if (onFlowsListPage()) return;
    openAccordion(['Automations']);
    await clickWhenReady(SIDEBAR_ROW);
    const arrived = await waitUntil(onFlowsListPage, 5000);
    if (!arrived) {
      console.warn(
        '[tour-guide] "create-automation-flow": never confirmed arrival on the automations list ' +
          '(route and/or Create automation button). The next step may resolve the wrong button.',
      );
    }
  },

  afterEnd: async () => {
    await closeFlowChrome(nameDialogOpen);

    if (!onFlowBuilderCanvas()) return;

    const neverSaved = onUnsavedNewFlow();
    if (!(await clickWhenReady(BACK_TO_LIST_BUTTON, 1500))) return;

    const guardAsked = await waitUntil(
      () => Boolean(resolveAnchor(DISCARD_CHANGES_BUTTON)),
      1200,
    );
    if (guardAsked) {
      await clickWhenReady(neverSaved ? DISCARD_CHANGES_BUTTON : KEEP_EDITING_BUTTON, 1500);
    }

    if (neverSaved || !guardAsked) await waitUntil(onFlowsListPage, 3000);
  },

  steps: [
    {
      id: 'open-composer',
      label: 'New flow',
      title: 'Start a new automation',
      description:
        'This is your automation list — every flow you have built, and whether it is live. A flow reacts to a trigger (a keyword, a form submission, a schedule) and then does something automatically: send a message, wait, branch, or hand off to a person. Press Next and I will start a WhatsApp automation.',
      anchor: CREATE_AUTOMATION_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: onFlowsListPage,
      prepare: async () => {},
      onNext: openChannelMenuAndPickWhatsApp,
      satisfied: nameDialogOpen,
      rewind: async () => {
        await closeFlowChrome(nameDialogOpen, 'rewind');
      },
      waitingHint: 'Press Next to start a new automation.',
    },

    {
      id: 'name-flow',
      label: 'Name it',
      title: 'Name your flow',
      description:
        'Filled in for you — every flow needs a name so you can find it again in your list later. You can rename it any time from the builder.',
      anchor: { selector: NAME_INPUT_SELECTOR },
      side: 'right',
      spotlight: true,
      fill: [[NAME_INPUT_SELECTOR, SUGGESTED_NAME]],
      satisfied: () => isFilled(NAME_INPUT_SELECTOR),
      waitingHint: 'Type a name to carry on.',
    },

    {
      id: 'confirm-create',
      label: 'Create',
      title: 'Open the builder',
      description:
        'Create flow opens the canvas. Every new flow starts with a Starting Step already on it — that is where a contact enters the automation.',
      anchor: CONFIRM_CREATE_BUTTON,
      side: 'top',
      spotlight: true,
      advance: 'auto',
      prepare: async () => {
        if (onFlowBuilderCanvas()) return;
        await clickWhenReady(CONFIRM_CREATE_BUTTON);
      },
      satisfied: onFlowBuilderCanvas,
      waitingHint: 'Opening the builder…',
    },

    {
      id: 'meet-start',
      label: 'Start',
      title: 'The Starting Step',
      description:
        'Every flow needs exactly one entry point — this is it. It fires when a contact\'s message matches its trigger (a keyword, right now). Next, drag a couple of steps onto the canvas so it actually does something.',
      anchor: { selector: '.react-flow__node-starting_step' },
      side: 'right',
      spotlight: true,
      forceHighlight: true,
      satisfied: () => hasNodeOfType('starting_step'),
    },

    {
      id: 'add-send-message',
      label: 'Send Message',
      title: 'Add a Send Message step',
      description:
        'Drag "Send Message" from the panel on the left onto the canvas. It sends the contact a WhatsApp message the moment the flow reaches it — the most common thing an automation does. Press Next and I will drop it on for you, or drag it yourself.',
      anchor: draggableLabelled('Send Message'),
      side: 'right',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      onNext: async () => {
        if (hasNodeOfType('send_message')) return;
        dropNodeOntoCanvas('send_message', 'Send Message');
        await waitUntil(() => hasNodeOfType('send_message'), 1500);
      },
      satisfied: () => hasNodeOfType('send_message'),
      waitingHint: 'Drag "Send Message" onto the canvas, or press Next to add it for you.',
    },

    {
      id: 'fill-message',
      label: 'Message text',
      title: 'Write the message',
      description:
        'Filled in for you — a Send Message node needs body text, or Run test flags it as an error. Edit it if you like, or leave it and press Next.',
      anchor: { selector: MESSAGE_FIELD_SELECTOR },
      side: 'left',
      spotlight: true,
      ready: () => onFlowBuilderCanvas() && hasNodeOfType('send_message'),
      fill: [[MESSAGE_FIELD_SELECTOR, DUMMY_MESSAGE]],
      satisfied: () => isFilled(MESSAGE_FIELD_SELECTOR),
      waitingHint: 'Type a message to carry on.',
      optional: true,
    },

    {
      id: 'connect-start-to-message',
      label: 'Connect Start',
      title: 'Connect Start to Send Message',
      description:
        'Drag from the small dot at the bottom of Starting Step to the dot at the top of Send Message. This wire is what tells the flow what happens next — nothing downstream of a node runs until it is actually connected. Press Next and I will connect them for you, or drag the wire yourself.',
      anchor: { selector: '.react-flow__node-starting_step .react-flow__handle.source' },
      side: 'right',
      spotlight: true,
      ready: () => onFlowBuilderCanvas() && hasNodeOfType('send_message'),
      prepare: async () => {
        startToMessageBaseline = edgeCount();
      },
      onNext: async () => {
        if (edgeCount() > startToMessageBaseline) return;
        const source = sourceHandleOf('starting_step');
        const target = targetHandleOf('send_message');
        if (source && target) {
          await connectHandles(source, target);
          await waitUntil(() => edgeCount() > startToMessageBaseline, 1500);
        }
      },
      satisfied: () => edgeCount() > startToMessageBaseline,
      waitingHint: 'Drag the connection yourself, or press Next and I will connect them.',
      optional: true,
    },

    {
      id: 'add-end',
      label: 'End',
      title: 'Add an End step',
      description:
        'Now drag "End" onto the canvas, underneath Send Message. It marks where this branch of the flow finishes — every flow should end somewhere explicit rather than trailing off. Press Next and I will drop it on for you, or drag it yourself.',
      anchor: draggableLabelled('End'),
      side: 'right',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      onNext: async () => {
        if (hasNodeOfType('end')) return;
        dropNodeOntoCanvas('end', 'End');
        await waitUntil(() => hasNodeOfType('end'), 1500);
      },
      satisfied: () => hasNodeOfType('end'),
      waitingHint: 'Drag "End" onto the canvas, or press Next to add it for you.',
    },

    {
      id: 'connect-message-to-end',
      label: 'Connect End',
      title: 'Connect Send Message to End',
      description:
        'Same gesture, one more time: drag from the dot at the bottom of Send Message to the dot at the top of End. That completes the flow — a message goes out, then the flow ends.',
      anchor: { selector: '.react-flow__node-send_message .react-flow__handle.source' },
      side: 'right',
      spotlight: true,
      ready: () => onFlowBuilderCanvas() && hasNodeOfType('end'),
      prepare: async () => {
        messageToEndBaseline = edgeCount();
      },
      onNext: async () => {
        if (edgeCount() > messageToEndBaseline) return;
        const source = sourceHandleOf('send_message');
        const target = targetHandleOf('end');
        if (source && target) {
          await connectHandles(source, target);
          await waitUntil(() => edgeCount() > messageToEndBaseline, 1500);
        }
      },
      satisfied: () => edgeCount() > messageToEndBaseline,
      waitingHint: 'Drag the connection yourself, or press Next and I will connect them.',
      optional: true,
    },

    {
      id: 'run-test',
      label: 'Check errors',
      title: 'Check for errors',
      description:
        'Click Run test. It opens a panel listing anything the flow is missing before it can go live — a broken connection, a node with nothing wired into it, an empty message. If it finds something, I will open that node and jump straight to the field causing it.',
      anchor: RUN_TEST_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      onNext: async () => {
        await clickWhenReady(RUN_TEST_BUTTON);
        const foundIssue = await waitUntil(() => Boolean(resolveAnchor(FIRST_ISSUE_FOCUS_BUTTON)), 400);
        if (!foundIssue) return;
        await clickWhenReady(FIRST_ISSUE_FOCUS_BUTTON);
        const fieldOpened = await waitUntil(() => Boolean(findVisible(MESSAGE_FIELD_SELECTOR)), 800);
        if (fieldOpened && !isFilled(MESSAGE_FIELD_SELECTOR)) {
          fillField(MESSAGE_FIELD_SELECTOR, DUMMY_MESSAGE);
        }
      },
      waitingHint: 'Press Next to check the flow for errors.',
      optional: true,
    },

    {
      id: 'save',
      label: 'Save',
      title: 'Save your flow',
      description:
        'Click Save. It is stored as a draft you can keep editing — nothing goes live until you press Publish separately, so there is no risk in saving early and often.',
      anchor: SAVE_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      onNext: async () => {
        await clickWhenReady(SAVE_BUTTON);
      },
      waitingHint: 'Press Next to save your flow.',
    },

    {
      id: 'back-to-list',
      label: 'Back to list',
      title: 'Back to your automations list',
      description:
        'Click the back arrow. Your flow is already saved as a draft — you can reopen it any time from the list to keep building, or hit Publish when it is ready to go live.',
      anchor: BACK_TO_LIST_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      onNext: async () => {
        if (onFlowsListPage()) return;
        await clickWhenReady(BACK_TO_LIST_BUTTON);
      },
      satisfied: onFlowsListPage,
      waitingHint: 'Press Next to return to your automations list.',
    },
  ],
};
